import { Button } from '@/components/ui/button.jsx'
import { Icon } from '@iconify/react'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion.jsx'
import { motion } from 'framer-motion'
import { handleError } from '@/services/errorHandler.js'
import { toast } from 'sonner'
import * as API from '@/services/api.js'
import { toggleLoader } from '@/hooks/useDashboardLoader.jsx'
import { useDashboardContext } from '@/context-state/DashboardContext.jsx'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { AnalyticsService } from '@/services/analytics.js'
import { NewReportDialog } from '@/layouts/dashboard/nav/subnavs/NewReportDialog.jsx'
import { TypographySmall, TypographySmallMuted } from '@/components/ui/typography.jsx'
import { AdAccountSelect } from '@/components/ad-account-select/AdAccountSelect.jsx'
import { UserRoles } from '@/consts/index.js'
import { RoleGuard } from '@/guards/RoleGuard.jsx'
import EngagingWrapper from '@/components/engaging-wrapper'

export const CreativeAnalyticsSubNavbar = () => {

    const { creativeContext, adAccountsContext } = useDashboardContext()
    const { addManyCreativeReportsToState, creativeReports } = creativeContext
    const [searchParams, setSearchParams] = useSearchParams()
    const { chosenAdAccount } = adAccountsContext
    const navigate = useNavigate()
    const location = useLocation()
    const superSegmentFromLocationState = location.state?.superSegment;
    const chosenReportId = searchParams.get('report')
    const superSegmentFromSearchParams = searchParams.get('superSegment')
    const [isNewReportDialogOpen, setIsNewReportDialogOpen] = useState(false)
    const pathname = window.location.pathname

    const createDefaultReports = async () => {
        try {
            toggleLoader(true)

            const defaultReportPropertiesOne = {
                order: 1,
                title: '🏆 Best Creatives',
                type: 'top-performing',
                description: "See exactly which ads are costing you and which ones are bringing in returns",
                metricKeys: ['spend', 'roas'],
                chartType: 'card',
                filters: [],
                groupBy: { key: 'ad_creative', label: "Creative" }
            }

            const defaultReportPropertiesTwo = {
                order: 2,
                title: '🎥 Winning Videos',
                type: 'top-performing',
                chartType: 'card',
                description: "Uncover where your ad spend is driving results—and where it's falling short.",
                metricKeys: ['3s_video_plays', 'video_avg_time_watched', 'thruplay'],
                filters: [[{ "optionId": "ad_type", "value": ["video"], "operatorValue": "is" }]
                ],
                groupBy: { key: 'ad_creative', label: "Creative" }
            }

            const defaultReportPropertiesThree = {
                order: 3,
                title: '🥇 Best Ad',
                type: 'top-performing',
                chartType: 'bar',
                description: "Break down your ad spend to reveal what's working and what's wasting budget.",
                metricKeys: ['spend', 'roas', 'impressions'],
                filters: [],
                groupBy: { key: 'ad_name', label: "Ad Name" }
            }

            const defaultReportPropertiesFour = {
                order: 4,
                title: '📚 Copy Winner',
                type: 'top-performing',
                description: "Discover which ads are generating profit and which are just draining your budget.",
                metricKeys: ['see_more_rate', 'clicks_all', 'impressions'],
                chartType: 'card',
                filters: [],
                groupBy: { key: 'ad_copy', label: "Copy" }
            }

            const defaultReportPropertiesFive = {
                order: 5,
                title: '✈️ Top Landing Page',
                type: 'top-performing',
                chartType: 'card',
                description: "Analyze your ad spend to maximize ROI and minimize waste.",
                metricKeys: ['spend', 'ctr_link_click', 'click_to_atc_ratio', 'impressions'],
                filters: [],
                groupBy: { key: 'ad_landing_page', label: "Landing Page" }
            }

            const defaultReportPropertiesSix = {
                order: 6,
                title: '🖼 Static In-Depth',
                type: 'top-performing',
                chartType: 'card',
                description: 'Uncover where your ad spend is driving results—and where its falling short',
                metricKeys: ['spend', 'ctr_link_click', 'click_to_atc_ratio', 'impressions'],
                filters: [[{ "optionId": "ad_type", "value": ["image"], "operatorValue": "is" }]],
                groupBy: { key: 'ad_creative', label: "Creative" }
            }

            const defaultReportPropertiesSeven = {
                order: 7,
                title: '📈 Ad Wear-Out',
                type: 'top-performing',
                chartType: 'line',
                description: 'Uncover where your ad spend is driving results—and where its falling short',
                metricKeys: ['spend', 'ctr_link_click'],
                filters: [],
                groupBy: { key: 'ad_creative', label: "Creative" }
            }

            const defaultReportPropertiesEight = {
                order: 8,
                type: 'custom-groups-comparison',
                title: '🎨 Compare Creatives Types',
                description: 'Uncover where your ad spend is driving results—and where its falling short',
                metricKeys: ['spend', 'roas', 'impressions'],
                filters: [],
                customGroups: [
                    {
                        "name": "Images",
                        "groupFilters": [
                            [
                                {
                                    "optionId": "ad_type",
                                    "value": [
                                        "image"
                                    ],
                                    "operatorValue": "is"
                                }
                            ]
                        ],
                        "id": "0.5747008092342629"
                    },
                    {
                        "name": "Videos",
                        "groupFilters": [
                            [
                                {
                                    "optionId": "ad_type",
                                    "value": [
                                        "video"
                                    ],
                                    "operatorValue": "is"
                                }
                            ]
                        ],
                        "id": "0.11007227429752242"
                    },
                    {
                        "name": "Carousels",
                        "groupFilters": [
                            [
                                {
                                    "optionId": "ad_type",
                                    "value": [
                                        "carousel"
                                    ],
                                    "operatorValue": "is"
                                }
                            ]
                        ],
                        "id": "0.5462102035853862"
                    }
                ],
                chartType: 'bar',
                groupBy: { key: 'ad_creative', label: "Creative" } // This is the default group by, not really in use
            }

            const allDefaultReports = await Promise.all([
                API.createCreativeReport(defaultReportPropertiesOne).then(({ data }) => data),
                API.createCreativeReport(defaultReportPropertiesTwo).then(({ data }) => data),
                API.createCreativeReport(defaultReportPropertiesThree).then(({ data }) => data),
                API.createCreativeReport(defaultReportPropertiesFour).then(({ data }) => data),
                API.createCreativeReport(defaultReportPropertiesFive).then(({ data }) => data),
                API.createCreativeReport(defaultReportPropertiesSix).then(({ data }) => data),
                API.createCreativeReport(defaultReportPropertiesSeven).then(({ data }) => data),
                API.createCreativeReport(defaultReportPropertiesEight).then(({ data }) => data),
            ])

            // Create the 5 default reports. So we will have a better landing to our users
            addManyCreativeReportsToState(allDefaultReports)
            const firstReport = allDefaultReports[0]
            navigate(`creative?report=${firstReport._id}`)
            // setSearchParams({ report: firstFiveDefaultReports[0]._id })

        } catch (e) {
            handleError(e)
            console.error(e)
            toast.error(e.message)
        } finally {
            toggleLoader(false)
        }
    }

    const createNewReportAndNavigateToIt = async (reportType) => {
        try {
            setIsNewReportDialogOpen(false)

            if (!chosenAdAccount) {
                toast.error('Please select an ad account first')
                return
            }

            toggleLoader(true)

            let description = 'See which creatives are achieving the best performance'
            let title = 'New Top Performing Report'
            let chartType = 'card'
            if (reportType === 'custom-groups-comparison') {
                description = 'Evaluate multiple sets of creatives against each other by filtering through custom groups'
                title = 'Custom Groups Comparison Report'
                chartType = 'bar'
            }

            if (reportType === 'super-segments') {
                description = 'Compare creatives by super segments - defined by your own words'
                title = '⚡ Super Segments Report'
                chartType = 'bar'
            }

            const defaultReportProperties = {
                order: creativeReports ? creativeReports.length + 1 : 1,
                title,
                type: reportType,
                description,
                chartType,
                metricKeys: ['spend', 'roas'],
                filters: [],
                groupBy: {
                    key: "ad_name",
                    label: "Ad Name"
                }
            }

            const { data } = await API.createCreativeReport(defaultReportProperties)

            addManyCreativeReportsToState([data])
            setSearchParams({ report: data._id })

            AnalyticsService.track('new-creative-report-created', { reportId: data._id })
            toast.success('Report created successfully')
        } catch (e) {
            handleError(e)
            console.error(e)
            toast.error(e.message)
        } finally {
            toggleLoader(false)
        }
    }

    const navigateToReport = (reportId, reportTitle) => {
        navigate(`creative?report=${reportId}`)
        AnalyticsService.track('selected-report-changed', { reportId, reportTitle })
    }

    useEffect(() => {
        // TODO - when clicking on the 'analytics' main icon from the 'insights' page it's not choosing a default report
        // will probably change that soon
        if (pathname.includes('insights')) {
            return
        }

        if (pathname.includes('video-explorer')) {
            return
        }

        // If reports not exists, create the defaults (Happen when the user switch to new space for the first time)
        const firstReport = creativeReports[0]
        if (!firstReport) {
            createDefaultReports()
            return
        }

        const superSegment = superSegmentFromLocationState || superSegmentFromSearchParams
        // Set the first report as default
        if (!chosenReportId) {
            navigate(`creative?report=${firstReport._id}${superSegment ? `&superSegment=${superSegment}` : ''}`)
            return
        }

        // Check it the report exists on the state (if the user switch spaces it won't be there)
        const reportExistsInCurrentState = creativeReports.find(report => report._id === chosenReportId)
        if (reportExistsInCurrentState) {
            navigate(`creative?report=${chosenReportId}${superSegment ? `&superSegment=${superSegment}` : ''}`)
            return
        }

        // The default - if the report doesn't exist in the state, navigate to the first report
        navigate(`creative?report=${firstReport._id}${superSegment ? `&superSegment=${superSegment}` : ''}`)

    }, [creativeReports, chosenReportId, pathname])

    return (
        <div className="p-4">

            <NewReportDialog
                isDialogOpen={isNewReportDialogOpen}
                setIsDialogOpen={setIsNewReportDialogOpen}
                onCreateNewReport={createNewReportAndNavigateToIt}
            />

            <AdAccountSelect />

            <div
                className={`flex items-center gap-2 relative transition-colors hover:bg-accent cursor-pointer p-2 rounded-md mb-2 ${pathname.includes('insights') ? 'bg-accent' : ''}`}
                onClick={() => navigate('creative/insights')}
            >
                <EngagingWrapper
                    type="sparkle"
                    elementId="creative-insights-page-btn"
                    oneTime={true}
                    storageType="local"
                >
                    <TypographySmallMuted className="flex items-center gap-2">
                        <Icon icon="ic:baseline-insights" className="h-5 w-5" />
                        <span>Insights</span>
                    </TypographySmallMuted>
                </EngagingWrapper>
            </div>

            <div
                className={`flex items-center gap-2 relative transition-colors hover:bg-accent cursor-pointer p-2 rounded-md mb-2 ${pathname.includes('video-explorer') ? 'bg-accent' : ''}`}
                onClick={() => navigate('creative/video-explorer')}
            >
                <TypographySmallMuted className="flex items-center gap-2">
                    <Icon icon="material-symbols:video-search-rounded" className="h-5 w-5" />
                    <span>Video Explorer</span>
                </TypographySmallMuted>
            </div>

            <div>

                <RoleGuard minimumRoleRequired={UserRoles.EDITOR}>
                    <Button
                        onClick={() => setIsNewReportDialogOpen(true)}
                        variant="primary-outline"
                        className="w-full justify-start gap-2 mb-2 px-2"
                    >
                        <Icon icon="icons8:plus" className="h-4 w-4" />
                        Create report
                    </Button>
                </RoleGuard>


                <Accordion
                    type="single"
                    collapsible
                    defaultValue={'reports'}
                    className="w-full px-2 font-light"
                >
                    <AccordionItem value="reports" className="border-0">
                        <AccordionTrigger>Reports</AccordionTrigger>
                        <AccordionContent>
                            <div
                                className="pl-2 space-y-1 border-0 overflow-y-scroll max-h-[70vh]"
                            >
                                {
                                    creativeReports
                                        .sort((a, b) => a.order - b.order)
                                        .map((report) => {
                                            const isChosen = chosenReportId === report._id
                                            return (
                                                <motion.div
                                                    key={report._id}
                                                    className={`flex items-center gap-2 relative transition-colors hover:bg-accent cursor-pointer p-2 rounded-md ${isChosen ? 'bg-accent' : ''}`}
                                                    initial={{ opacity: 0, left: '-30px' }}
                                                    whileInView={{ opacity: 1, left: 0 }}
                                                    onClick={() => navigateToReport(report._id, report.title)}
                                                >
                                                    <TypographySmall className="truncate leading-5">{report.title}</TypographySmall>
                                                </motion.div>
                                            )
                                        })
                                }
                            </div>
                        </AccordionContent>
                    </AccordionItem>
                </Accordion>
            </div>


        </div>
    )
}